/**
 * Copyright (c) 2022 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import * as React from 'react';
import { flatMap, groupBy, sortBy } from 'lodash';
import { useContext } from 'react';
import { Field } from 'react-final-form';
import { StyleSheet, css } from 'aphrodite';

import { ActionButton, Dropdown, isEmptyValue } from '@blend/enterprise-components';
import { MeContext } from 'blb-contexts/Me';
import { FormCard } from 'blb-components/FormCard';
import { WrappedInput } from 'blb-components/WrappedInput';
import { InnerContainer, MiddleContainer, Header, OuterContainer } from 'blb-components/IdentityLike';
import { openUrlInSameTab } from 'blb-utils/misc';
import { getRedirectURLForLendingPivot } from 'blb-utils/identity';
import { FinalFormInputConfig } from 'blb-ui-types/helpers';
import { logout } from 'blb-api/auth';

interface TenantSelectionFormValues {
  tenantId: string;
}

const styles = StyleSheet.create({
  buttonWrapper: {
    ':not(:last-child)': {
      marginRight: 12,
    },
  },
});

export const SelectTenantPage: React.FC<{}> = () => {
  const me = useContext(MeContext)!;

  const onSubmit = (formValues: TenantSelectionFormValues) => {
    openUrlInSameTab(getRedirectURLForLendingPivot(formValues.tenantId, me.principalId));
  };

  const tenantDisplayOptions = flatMap(groupBy(me.tenantGroups, 'friendlyName'), friendlyNameGroup =>
    friendlyNameGroup.map(tenantGroupEntry => {
      // use the friendly name if possible, and disambiguate multiple tenants with
      // the same friendly name by appending the tenant (group) name itself
      const displayName = tenantGroupEntry.friendlyName || tenantGroupEntry.tenantGroup;
      return {
        text: friendlyNameGroup.length > 1 ? `${displayName} (${tenantGroupEntry.tenantGroup})` : displayName,
        value: tenantGroupEntry.tenantGroup,
      };
    }),
  );

  return (
    <OuterContainer>
      <MiddleContainer>
        <InnerContainer>
          <Header />
          <FormCard<TenantSelectionFormValues>
            additionalFooterComponent={
              <div className={css(styles.buttonWrapper)}>
                <ActionButton id="it-SelectTenant-cancel" label="Cancel" onClick={logout} theme="tertiary" />
              </div>
            }
            headerTitle="Welcome"
            id="SelectTenant"
            onSubmit={onSubmit}
            showCancel={false}
            submitLabel="Proceed"
          >
            <Field
              {...({
                autoFocus: true,
                component: WrappedInput,
                customComponentProps: {
                  options: sortBy(tenantDisplayOptions, 'text'),
                },
                inputComponent: Dropdown,
                label: 'Which lender are you working with?',
                labelSize: 'sm',
                name: 'tenantId',
                validate: isEmptyValue('Please select a lender.'),
              } as FinalFormInputConfig<Dropdown.Props, string>)}
            />
          </FormCard>
        </InnerContainer>
      </MiddleContainer>
    </OuterContainer>
  );
};
